// src/components/TimerModal.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { IoClose, IoPlay, IoPause, IoRefresh, IoStop } from 'react-icons/io5';
import { useAppContext } from '../context/AppContext';

// Rutas a los archivos de sonido en la carpeta /public/sounds/
const COUNTDOWN_BEEP_SOUND = '/sounds/countdown-beep.mp3';
const GO_SOUND = '/sounds/go-sound.mp3';
const REST_SOUND = '/sounds/rest-sound.mp3';
const FINISHED_SOUND = '/sounds/finished-sound.mp3';

const TimerModal = () => {
    const { isTimerOpen, closeTimer } = useAppContext();

    const [settings, setSettings] = useState({
        prepare: 10,
        work: 20,
        rest: 10,
        rounds: 8,
        cooldown: 120,
    });

    const [phase, setPhase] = useState('idle');
    const [currentRound, setCurrentRound] = useState(1);
    const [timeLeft, setTimeLeft] = useState(settings.prepare);
    const [isRunning, setIsRunning] = useState(false);

    const countdownSound = useMemo(() => new Audio(COUNTDOWN_BEEP_SOUND), []);
    const goSound = useMemo(() => new Audio(GO_SOUND), []);
    const restSound = useMemo(() => new Audio(REST_SOUND), []);
    const finishedSound = useMemo(() => new Audio(FINISHED_SOUND), []);

    // --- LÓGICA DE TICK CON CONTEO DE RONDAS CORREGIDO ---
    const tick = useCallback(() => {
        setTimeLeft(prevTime => {
            if (prevTime > 1) {
                if (prevTime <= 4) countdownSound.play();
                return prevTime - 1;
            }

            if (phase === 'preparing') {
                goSound.play();
                setPhase('working');
                return settings.work;
            } else if (phase === 'working') {
                const nextRound = currentRound + 1;
                if (nextRound > settings.rounds) {
                    restSound.play();
                    setPhase('cooldown');
                    return settings.cooldown;
                }
                restSound.play();
                setCurrentRound(nextRound);
                setPhase('resting');
                return settings.rest;
            } else if (phase === 'resting') {
                goSound.play();
                setPhase('working');
                return settings.work;
            } else if (phase === 'cooldown') {
                finishedSound.play();
                setPhase('finished');
                setIsRunning(false);
                return 0;
            }
            return 0;
        });
    }, [phase, settings, currentRound, countdownSound, goSound, restSound, finishedSound]);

    useEffect(() => {
        let timerId = null;
        if (isRunning && phase !== 'finished' && phase !== 'cancelled') {
            timerId = setInterval(tick, 1000);
        }
        return () => clearInterval(timerId);
    }, [isRunning, tick, phase]);

    // --- FUNCIÓN DE RESET MEJORADA PARA DETENER SONIDOS ---
    const handleReset = useCallback(() => {
        setIsRunning(false);
        setPhase('idle');
        setCurrentRound(1);
        setTimeLeft(settings.prepare);

        // Detener y rebobinar todos los sonidos para que no queden en segundo plano
        [countdownSound, goSound, restSound, finishedSound].forEach(sound => {
            sound.pause();
            sound.currentTime = 0;
        });
    }, [settings.prepare, countdownSound, goSound, restSound, finishedSound]);


    // --- NUEVO USEEFFECT PARA LIMPIAR AL CERRAR EL MODAL ---
    useEffect(() => {
        if (!isTimerOpen) {
            handleReset();
        }
    }, [isTimerOpen, handleReset]);


    const handleStartPause = () => {
        if (phase === 'idle' || phase === 'finished' || phase === 'cancelled') {
            setCurrentRound(1);
            setPhase('preparing');
            setTimeLeft(settings.prepare);
        }
        setIsRunning(!isRunning);
    };

    const handleStop = () => {
        setIsRunning(false);
        setPhase('cancelled');
        setTimeLeft(0);
        finishedSound.play();
    };
    
    const handleSettingsChange = (e) => {
        const { name, value } = e.target;
        if (!isRunning) {
            setSettings(prev => ({...prev, [name]: parseInt(value, 10) || 0 }));
        }
    };
    
    // Sincronizar timeLeft si 'prepare' cambia mientras está en idle
    useEffect(() => {
        if (phase === 'idle') {
            setTimeLeft(settings.prepare);
        }
    }, [settings.prepare, phase]);


    if (!isTimerOpen) return null;

    // Lógica para el texto y color de la pantalla
    let currentPhaseInfo;
    if (!isRunning && (phase !== 'idle' && phase !== 'finished' && phase !== 'cancelled')) {
        currentPhaseInfo = { text: "Pausado", color: "text-orange-400", bg: "bg-orange-900" };
    } else {
        const phaseMap = {
            idle: { text: "Listo para empezar", color: "text-gray-400", bg: "bg-gray-700" },
            preparing: { text: "Prepárate", color: "text-yellow-400", bg: "bg-yellow-900" },
            working: { text: "¡ACCIÓN!", color: "text-green-400", bg: "bg-green-900" },
            resting: { text: "Descansa", color: "text-blue-400", bg: "bg-blue-900" },
            cooldown: { text: "Enfriamiento", color: "text-purple-400", bg: "bg-purple-900" },
            finished: { text: "¡Completado!", color: "text-white", bg: "bg-green-600" },
            cancelled: { text: "Cancelado", color: "text-white", bg: "bg-red-700" },
        };
        currentPhaseInfo = phaseMap[phase];
    }
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 p-4">
            <div className="bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-md">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold text-white">Temporizador</h2>
                    <button onClick={closeTimer} className="text-gray-400 hover:text-white"><IoClose size={28} /></button>
                </div>

                <div className={`p-8 rounded-lg text-center transition-colors duration-500 ${currentPhaseInfo.bg}`}>
                    <p className={`text-3xl font-bold ${currentPhaseInfo.color}`}>{currentPhaseInfo.text}</p>
                    <p className="text-8xl font-mono font-bold text-white my-4">{timeLeft}</p>
                    <p className="text-xl text-gray-300">Ronda: {currentRound} / {settings.rounds}</p>
                </div>

                <div className="flex justify-center space-x-4 my-6">
                    <button onClick={handleStartPause} className="bg-green-600 hover:bg-green-500 text-white font-bold p-4 rounded-full disabled:opacity-50" disabled={phase === 'finished' || phase === 'cancelled'}>
                        {isRunning ? <IoPause size={32} /> : <IoPlay size={32} />}
                    </button>
                    <button onClick={handleStop} className="bg-red-600 hover:bg-red-500 text-white font-bold p-4 rounded-full disabled:opacity-50" disabled={!isRunning && (phase !== 'preparing' && phase !== 'working' && phase !== 'resting' && phase !== 'cooldown')}>
                        <IoStop size={32} />
                    </button>
                    <button onClick={handleReset} className="bg-orange-500 hover:bg-orange-400 text-white font-bold p-4 rounded-full">
                        <IoRefresh size={32} />
                    </button>
                </div>

                <div className="grid grid-cols-2 gap-4 text-white">
                    <div><label>Preparación (s)</label><input type="number" name="prepare" value={settings.prepare} onChange={handleSettingsChange} disabled={isRunning} className="w-full bg-gray-700 p-2 rounded mt-1" /></div>
                    <div><label>Acción (s)</label><input type="number" name="work" value={settings.work} onChange={handleSettingsChange} disabled={isRunning} className="w-full bg-gray-700 p-2 rounded mt-1" /></div>
                    <div><label>Descanso (s)</label><input type="number" name="rest" value={settings.rest} onChange={handleSettingsChange} disabled={isRunning} className="w-full bg-gray-700 p-2 rounded mt-1" /></div>
                    <div><label>Rondas</label><input type="number" name="rounds" value={settings.rounds} onChange={handleSettingsChange} disabled={isRunning} className="w-full bg-gray-700 p-2 rounded mt-1" /></div>
                    <div className="col-span-2"><label>Enfriamiento Final (s)</label><input type="number" name="cooldown" value={settings.cooldown} onChange={handleSettingsChange} disabled={isRunning} className="w-full bg-gray-700 p-2 rounded mt-1" /></div>
                </div>
            </div>
        </div>
    );
};

export default TimerModal;